import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import proptypes from 'prop-types';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { withDynamicComponent } from '@thd-nucleus/app-render';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import {
  useDataModel,
  params,
  extend,
  shape as dsShape,
  string as dsString,
  QueryProvider,
  number as dsNumber,
} from '@thd-nucleus/data-sources';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import {
  triggerNewRelic,
  triggerNewRelicTimeLog,
  triggerTargetingInputsMetric
} from '../../utils/newRelicHelpers';
import './SponsoredPipBanner.scss';
import { initImpressionWatchers } from '../../utils/initImpressionWatchers';
import { bannerTypes, schemas, bannerImplementationTypes, PIP_MOBILEAPP_ENABLED } from '../../constants/constants';
import { newRelicConstants } from '../../constants';
import { buildRevJetBanner } from '../../utils/revjet';
import useFbtSkuListener from '../../hooks/useFbtSkuListener';
import { setAppId } from '../../utils/queryParamsHelpers';
import { isServer } from '../../utils/helpers';
import { determineAdServer } from '../../utils/utils';

const { string, shape, bool } = proptypes;

const SponsoredPipBannerComponent = (props) => {
  let { browserId, pageContext, renderHarmony, noPipSpacing = false } = props;
  let { mcvisID, guestUserID } = useThdCustomer();

  const [hasData, setHasData] = useState(false);
  const experienceContext = useContext(ExperienceContext);
  const schema = schemas.PIP_SPONSORED;
  const schemaId = `${schema}__banner_${pageContext.data.treatment}`;
  const maxWidth = experienceContext.channel === 'desktop' ? '100%' : '450px';
  const isMobile = experienceContext.channel === 'mobile';

  const bannerType = bannerTypes.MIDDLE;
  let marketingId = browserId || mcvisID || '';
  if (renderHarmony) {
    marketingId = '1234';
    guestUserID = '4321';
  }
  const pageType = schema;

  const customerType = useMemo(() => {
    return experienceContext.customer?.type ? experienceContext.customer.type : 'b2c';
  }, [experienceContext.customer?.type]);
  const appId = useMemo(() => setAppId({ experienceContext }), [experienceContext?.channel]);
  const isPipMobileAppEnabled = useConfigService(PIP_MOBILEAPP_ENABLED) || false;

  const customerIdentifier = guestUserID;
  const anchorSku = pageContext.data.itemId;
  const { fbtSkus } = useFbtSkuListener(pageContext?.data?.itemId);
  const requestConfig = {
    mcvisID,
    guestUserID,
    browserId,
    schema,
    schemaId,
    experienceContext,
    pageContext,
    setHasData,
    bannerType: bannerTypes.MIDDLE,
    eventBusData: { fbtSkus }
  };
  const skip = appId === 'mobileapp' && !isPipMobileAppEnabled;
  const apiRequestTimestamp = new Date().getTime();
  const experienceMetadata = isServer() ? undefined : window.experienceMetadata;
  const { data, loading, error } = useDataModel('sponsored', {
    skip,
    ssr: false,
    variables: {
      bannerType,
      marketingId,
      pageType,
      customerType,
      channel: appId,
      customerIdentifier,
      anchorSku,
      experienceName: experienceMetadata?.name,
      experienceVersion: experienceMetadata?.version
    }
  });

  const silentDegradationMetricRef = useRef(false);

  useEffect(() => {
    if (!skip && !silentDegradationMetricRef.current) {
      triggerTargetingInputsMetric({
        schema: pageType,
        bannerType,
        hasMatchProducts: !!anchorSku,
        hasCustomerIdentifier: !!customerIdentifier,
        implementation: bannerImplementationTypes.LEGACY,
      });
      silentDegradationMetricRef.current = true;
    }
  }, [skip, pageType, bannerType, anchorSku, customerIdentifier]);

  useEffect(() => {
    let apiResponseTimestamp;
    const adType = newRelicConstants.PIP_BANNER;
    if (!loading && !error && data?.sponsored?.banner) {
      apiResponseTimestamp = new Date().getTime();
      requestConfig.startTime = apiRequestTimestamp;
      setHasData(true);
      const sponsoredData = data.sponsored;
      buildRevJetBanner({ data: sponsoredData, requestConfig, adType });
    }
    if (error) {
      triggerNewRelic(adType, 'BANNER-API-FAILURE');
    } else if (data?.sponsored) {
      const bannerApiAdServer = determineAdServer(data.sponsored);
      triggerNewRelic(adType, 'BANNER-API-200');
      // SEND TIMESTAMPS TO CALCULATE API RESPONSE TIME
      triggerNewRelicTimeLog(newRelicConstants.PIP_BANNER_API_RESPONSE, apiRequestTimestamp, apiResponseTimestamp, bannerApiAdServer);
      // SEND TIME FROM BANNER INITIATED TO BANNER WINNER RECEIVED
      triggerNewRelicTimeLog(newRelicConstants.PIP_BANNER_AD_INIT_TO_WINNER, apiRequestTimestamp, apiResponseTimestamp, bannerApiAdServer);
    } else {
      triggerNewRelic(adType, 'BANNER-API-204');
    }
  }, [loading, error, data]);

  useEffect(() => {
    initImpressionWatchers();
  }, [hasData]);

  const containerClass = useMemo(() => {
    if (noPipSpacing) {
      return isMobile ? 'sui-pb-4 sui-mx-auto' : 'sui-pb-8 sui-mx-auto';
    }
    return 'sponsored_pip_banner_container';
  }, [noPipSpacing, isMobile]);

  return (hasData && (
    <div
      data-component="SponsoredPipBanner"
      className={containerClass}
      style={{ maxWidth }}
    >
      <div id={schemaId} data-testid="sponsored-pip-banner" className="sponsored_pip_banner" />
    </div>
  ));
};
const propTypes = {
  browserId: string,
  noPipSpacing: bool,
  pageContext: shape({
    label: string,
    treatment: string,
    data: shape({
      itemId: Number
    })
  }),
  renderHarmony: bool
};

const defaultProps = {
  browserId: '',
  pageContext: {},
  renderHarmony: false
};

SponsoredPipBannerComponent.propTypes = propTypes;
SponsoredPipBannerComponent.defaultProps = defaultProps;
SponsoredPipBannerComponent.dataModel = extend({
  sponsored: params({
    bannerType: dsString(),
    marketingId: dsString().isRequired(),
    pageType: dsString().isRequired(),
    customerType: dsString(),
    channel: dsString().isRequired(),
    customerIdentifier: dsString(),
    anchorSku: dsNumber({ float: true }),
    experienceName: dsString(),
    experienceVersion: dsString()
  }).shape({
    banner: dsShape({
      bannerURL: dsString(),
      clickthruURL: dsString(),
      onClickBeacon: dsString(),
      clickConversionBeacon: dsString(),
      viewConversionBeacon: dsString(),
      onViewBeacon: dsString(),
      slotId: dsString(),
      campaignId: dsString(),
      placementId: dsString(),
      flightId: dsString(),
      innervateKey: dsString(),
      innervateTag: dsString()
    }),
    customerRecentlyViewedSkus: dsShape({
      skus: dsString(),
      refreshAt: dsString()
    })
  })
});

const QueryProvidedSponsoredPipBanner = (props) => {

  const skipFn = ({ skip, queryName }) => {
    return skip;
  };

  return (
    <QueryProvider cacheKey="sponsored-pip-banner" skip={skipFn}>
      <SponsoredPipBannerComponent {...props} />
    </QueryProvider>
  );
};
QueryProvidedSponsoredPipBanner.propTypes = propTypes;

QueryProvidedSponsoredPipBanner.defaultProps = defaultProps;
QueryProvidedSponsoredPipBanner.dataModel = extend({}, SponsoredPipBannerComponent);
const SponsoredPipBanner = withErrorBoundary(
  withDynamicComponent(
    withHydrator(
      {
        id: 'hydrated-sponsored-pip-banner',
        className: 'sponsored-pip-banner-hydrator',
        scrollBuffer: 100
      },
      QueryProvidedSponsoredPipBanner
    )
  )
);
SponsoredPipBanner.displayName = 'SponsoredPipBanner';
export { SponsoredPipBanner };
